import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Box from "@mui/material/Box";
import { enqueueSnackbar } from "notistack";
import React, { ReactElement, useState } from "react";

import { Asset, Collection } from "../../../generated/gql/graphql";
import useCollectionAssets from "../../../hooks/mutation/collection/use-collection-assets";
import useText from "../../../hooks/use-text";
import { LocalizedStrings, SupportedLanguages } from "../../../utils/lang";

type Props = {
  assets: Asset[];
  collection: Collection;
  open: boolean;
  onClose(): void;
};

export default function RemoveAssetsFromCollectionDialog(
  props: Props
): ReactElement {
  const { assets, open, collection, onClose } = props;

  const { strings } = useText(local);
  const [isLoading, setIsLoading] = useState(false);
  const { deleteCollectionAssets } = useCollectionAssets();

  async function removeFromCollection(): Promise<void> {
    try {
      setIsLoading(true);
      await deleteCollectionAssets({
        collectionId: collection.id,
        assetIds: assets.map((v) => v.id),
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(strings.error, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {strings.title
          .replace("{{count}}", assets.length.toString())
          .replace("{{collection_name}}", collection.name || collection.id)}
      </DialogTitle>
      <DialogContent className="min-w-[300px]">
        <ul className="max-h-40 space-y-5 bg-gray-100 p-5">
          {assets.map((asset: Asset) => (
            <li key={asset.id}>
              <p className="text-sm">{asset.name}</p>
            </li>
          ))}
        </ul>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{strings.cancel}</Button>
        <Button onClick={removeFromCollection}>
          {isLoading ? (
            <Box className="flex justify-center">
              <CircularProgress size="1rem" className="w-1 h-1" />
            </Box>
          ) : (
            strings.download
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type LocalSchema = {
  title: string;
  cancel: string;
  download: string;
  error: string;
};

const local: LocalizedStrings<LocalSchema, SupportedLanguages> = {
  default: {
    title: "Remove Assets from Collection '{{collection_name}}' ({{count}})",
    cancel: "Cancel",
    download: "Remove",
    error: "Failed to remove assets from collection. Please try it again",
  },
};
